/**
 * Native bridge for iOS / Android via Capacitor.
 *
 * This module is the single integration surface between the existing Hines Time
 * React codebase and Capacitor's native plugins. When running in a browser
 * (preview / production web) every export below is a no-op so the web build
 * is unaffected.
 *
 * Usage from React components:
 *
 *   import { isNative, getCurrentPosition, registerForPush, biometricSignIn } from '@/native';
 *
 *   if (isNative()) {
 *     const pos = await getCurrentPosition();
 *     ...
 *   }
 *
 * Add new plugin wrappers here so the rest of the app stays platform-agnostic.
 */

import { Capacitor } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
export const isIOS = () => platform() === 'ios';

// ---------------------------------------------------------------------------
// Geolocation — used for clock-in/out GPS verification
// ---------------------------------------------------------------------------
//
// Mirrors navigator.geolocation.getCurrentPosition so existing Home.jsx code
// keeps working unchanged. On native it routes through the Capacitor plugin
// which surfaces iOS permission dialogs and is far more reliable than the
// WebView's HTML5 Geolocation API.

export async function getCurrentPosition(options = {}) {
  if (!isNative()) {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }
  const { Geolocation } = await import('@capacitor/geolocation');
  // Capacitor returns { coords: { latitude, longitude, accuracy, ... } }
  return Geolocation.getCurrentPosition({
    enableHighAccuracy: options.enableHighAccuracy ?? true,
    timeout: options.timeout ?? 10000,
    maximumAge: options.maximumAge ?? 0,
  });
}

export async function watchPosition(callback, errorCallback, options = {}) {
  if (!isNative()) {
    return navigator.geolocation.watchPosition(callback, errorCallback, options);
  }
  const { Geolocation } = await import('@capacitor/geolocation');
  return Geolocation.watchPosition(
    { enableHighAccuracy: options.enableHighAccuracy ?? true },
    (position, err) => (err ? errorCallback?.(err) : callback(position)),
  );
}

// ---------------------------------------------------------------------------
// Camera — used for job photo uploads
// ---------------------------------------------------------------------------

export async function takePhoto({ quality = 80, allowEditing = false } = {}) {
  if (!isNative()) {
    // On web we fall back to a file input. Caller can also use <input type=file capture>.
    return null;
  }
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  return Camera.getPhoto({
    quality,
    allowEditing,
    resultType: CameraResultType.DataUrl,    // base64 string ready for upload
    source: CameraSource.Camera,
    saveToGallery: false,
  });
}

export async function pickPhoto({ quality = 80 } = {}) {
  if (!isNative()) return null;
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  return Camera.getPhoto({
    quality,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Photos,
  });
}

// ---------------------------------------------------------------------------
// Push Notifications — used for job assignments, reassignments, schedule alerts
// ---------------------------------------------------------------------------
//
// Returns the APNs device token (iOS) or FCM registration token (Android).
// Your backend should POST this to `/api/users/me/push-token` so it can target
// the device when dispatching job-assignment notifications.

export async function registerForPush(onToken, onError) {
  if (!isNative()) return null;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    onError?.(new Error('Push permission denied'));
    return null;
  }

  PushNotifications.addListener('registration', (token) => {
    onToken?.(token.value);
  });
  PushNotifications.addListener('registrationError', (err) => {
    onError?.(err);
  });

  await PushNotifications.register();
  return PushNotifications;
}

/**
 * High-level helper used right after a successful sign-in: requests push
 * permission, grabs the device token, and POSTs it to the backend so the
 * server can target this device with job-assignment / reassignment alerts.
 *
 * Safe to call on web — it's a no-op.
 */
export async function syncPushTokenWithBackend(apiUrl, jwt) {
  if (!isNative() || !apiUrl || !jwt) return null;
  return new Promise((resolve) => {
    let resolved = false;
    const done = (value) => { if (!resolved) { resolved = true; resolve(value); } };
    registerForPush(
      async (token) => {
        try {
          await fetch(`${apiUrl}/api/users/me/push-token`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({ token, platform: platform() }),
          });
          done(token);
        } catch (e) {
          console.warn('push-token sync failed:', e);
          done(null);
        }
      },
      (err) => {
        console.warn('push registration failed:', err);
        done(null);
      },
    );
    // Push permission dialog never resolves on declined web preview — give up after 10s
    setTimeout(() => done(null), 10000);
  });
}

export async function clearPushTokenOnBackend(apiUrl, jwt) {
  if (!isNative() || !apiUrl || !jwt) return;
  try {
    await fetch(`${apiUrl}/api/users/me/push-token`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${jwt}` },
    });
  } catch (e) {
    console.warn('push-token clear failed:', e);
  }
}

export async function onPushReceived(callback) {
  if (!isNative()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  PushNotifications.addListener('pushNotificationReceived', callback);
}

export async function onPushTapped(callback) {
  if (!isNative()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  PushNotifications.addListener('pushNotificationActionPerformed', callback);
}

// ---------------------------------------------------------------------------
// Biometric / Face ID sign-in
// ---------------------------------------------------------------------------
//
// On iOS this triggers a native Face ID / Touch ID prompt. After the user
// authenticates we read the stored JWT from secure storage and return it.
// First-time setup must call `storeBiometricToken(jwt)` after a successful
// password login.
//
// Plugin: `@capgo/capacitor-native-biometric` (LGPL, app-store-safe).

export async function biometricSignIn() {
  if (!isNative()) return null;
  const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
  const result = await NativeBiometric.isAvailable();
  if (!result.isAvailable) return null;

  await NativeBiometric.verifyIdentity({
    reason: 'Sign in to Hines Time',
    title: 'Hines Time',
    subtitle: 'Use Face ID to sign in securely',
    description: 'Your credentials are stored encrypted on this device.',
  });
  const creds = await NativeBiometric.getCredentials({ server: 'hinestime.com' });
  return creds; // { username, password } — we store the JWT in the password slot
}

/**
 * Returns true if the device has biometric hardware AND we've previously
 * stored credentials for it. Used by the SignIn screen to decide whether to
 * render the "Sign in with Face ID / Touch ID" shortcut button.
 */
export async function hasBiometricCredentials() {
  if (!isNative()) return false;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const avail = await NativeBiometric.isAvailable();
    if (!avail.isAvailable) return false;
    const creds = await NativeBiometric.getCredentials({ server: 'hinestime.com' });
    return !!(creds && creds.username && creds.password);
  } catch {
    return false;
  }
}

export async function storeBiometricToken(email, jwt) {
  if (!isNative()) return false;
  const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
  await NativeBiometric.setCredentials({
    username: email,
    password: jwt,                     // we store the JWT in the password slot
    server: 'hinestime.com',
  });
  return true;
}

export async function clearBiometricToken() {
  if (!isNative()) return;
  const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
  await NativeBiometric.deleteCredentials({ server: 'hinestime.com' });
}

// ---------------------------------------------------------------------------
// Status bar + splash screen helpers (cosmetic, called from App.js bootstrap)
// ---------------------------------------------------------------------------

export async function configureNativeUI() {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0B1321' });
  } catch (e) {
    // StatusBar plugin not available — non-fatal.
    console.warn('StatusBar configuration skipped:', e);
  }
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    console.warn('SplashScreen hide skipped:', e);
  }
}
